/**
 * 통합관리자 — 제품 현황
 * ---------------------------------------------------------------------------
 * "어느 기업이 어떤 시스템을 쓰고 있는가" 에 답하는 화면이다.
 *
 * 세 가지를 한곳에서 본다.
 *   1. 제품별 규모 — 구독 기업 수, 사용자, 월 구독 금액, 서버 상태
 *   2. 제품을 고르면 그 제품을 쓰는 기업 목록과 사용량
 *   3. 기업 × 제품 매트릭스 — 누가 무엇을 쓰는지 한눈에
 *
 * 제품이 별도 앱으로 흩어져 있어 플랫폼이 제품 안의 숫자를 직접 알 수 없다.
 * 제품이 보고한 값만 보이며, 보고가 없으면 "보고 없음" 으로 남긴다.
 * 0 으로 꾸며 내면 "정말 0건" 과 구분되지 않는다.
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App as AntdApp,
  Badge,
  Button,
  Card,
  Col,
  Empty,
  Checkbox,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Statistic,
  Switch,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  ApiOutlined,
  KeyOutlined,
  LinkOutlined,
  PlusOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { FormInstance } from 'antd';
import { apiClient } from '../../lib/api-client';
import { errorText } from '../../lib/queries';
import { CodeTag, useCodes } from '../../lib/codes';

const { Text, Paragraph } = Typography;

/* =========================================================================
 * 형
 * ====================================================================== */

interface ProductSummary {
  productCode: string;
  productName: string;
  description: string | null;
  iconName: string | null;
  marketingUrl: string | null;
  deploymentType: 'PLATFORM' | 'EXTERNAL';
  baseUrl: string | null;
  healthUrl: string | null;
  dbName: string | null;
  vendor: string | null;
  isActive: boolean;
  sortOrder: number;
  tenantCount: number;
  attentionCount: number;
  trialCount: number;
  userCount: number;
  monthlyRevenue: number;
  moduleCount: number;
  planCount: number;
}

interface ProductTenant {
  tenantId: string;
  companyName: string;
  slug: string;
  tenantStatus: string;
  subscriptionStatus: string;
  planCode: string;
  planName: string;
  units: number;
  startedAt: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  userCount: number;
  storageBytes: number;
  lastLoginAt: string | null;
  metrics: Record<string, number>;
  reportedAt: string | null;
}

interface Matrix {
  products: Array<{ productCode: string; productName: string }>;
  tenants: Array<{
    tenantId: string;
    companyName: string;
    slug: string;
    status: string;
    products: Record<string, { status: string; planName: string }>;
  }>;
}

interface ProductKey {
  productKeyId: string;
  keyLabel: string;
  keyPrefix: string;
  lastUsedAt: string | null;
  expiresAt: string | null;
  revokedAt: string | null;
  createdAt: string;
}

/** 등록·수정 폼이 다루는 값. 제품 요약에서 화면에만 쓰는 필드를 뺀 것이다. */
interface ProductFormValues {
  productCode: string;
  productName: string;
  description?: string | null;
  iconName?: string | null;
  marketingUrl?: string | null;
  deploymentType: 'PLATFORM' | 'EXTERNAL';
  baseUrl?: string | null;
  healthUrl?: string | null;
  dbName?: string | null;
  vendor?: string | null;
  sortOrder: number;
  isActive: boolean;
}

interface Health {
  productCode: string;
  healthUrl: string | null;
  reachable: boolean;
  status: number | null;
  latencyMs: number | null;
  detail: string | null;
}

/* =========================================================================
 * 표시 도우미
 * ====================================================================== */

/**
 * 표시명과 색은 공통코드가 정한다(「시스템 관리 > 공통코드」).
 *
 * PRODUCT_USAGE_METRIC 은 제품이 스스로 보고하는 사용량 지표의 이름이다.
 * 제품이 새 지표를 보내기 시작하면 공통코드에 한 줄을 더하면 되고, 없으면
 * 키가 그대로 보인다 — 배포를 기다릴 일이 아니다.
 */
const CODE_GROUPS = ['SUBSCRIPTION_STATUS', 'PRODUCT_USAGE_METRIC'];

function formatBytes(bytes: number): string {
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(i === 0 ? 0 : 1)}${units[i]}`;
}

function formatDate(iso: string | null): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('ko-KR');
}

/** 보고 시각이 오래되면 배치가 멈춘 것이다. 색으로 알린다. */
function reportFreshness(iso: string | null): { text: string; color: string } {
  if (!iso) return { text: '보고 없음', color: 'default' };

  const hours = (Date.now() - new Date(iso).getTime()) / 3_600_000;
  if (hours < 26) return { text: formatDate(iso), color: 'green' };
  if (hours < 24 * 7) return { text: formatDate(iso), color: 'orange' };
  return { text: formatDate(iso), color: 'red' };
}

/* =========================================================================
 * 화면
 * ====================================================================== */

export default function ProductPage() {
  const { message, modal } = AntdApp.useApp();
  const queryClient = useQueryClient();
  const codes = useCodes(CODE_GROUPS);

  const [selected, setSelected] = useState<string | null>(null);
  const [editing, setEditing] = useState<ProductSummary | 'new' | null>(null);
  const [keyTarget, setKeyTarget] = useState<string | null>(null);
  const [form] = Form.useForm<ProductFormValues>();

  const products = useQuery<ProductSummary[]>({
    queryKey: ['admin', 'products'],
    queryFn: async () =>
      (await apiClient.get<ProductSummary[]>('/admin/products', { params: { includeInactive: true } })).data,
  });

  const tenants = useQuery<ProductTenant[]>({
    queryKey: ['admin', 'products', selected, 'tenants'],
    queryFn: async () =>
      (await apiClient.get<ProductTenant[]>(`/admin/products/${selected}/tenants`)).data,
    enabled: !!selected,
  });

  const matrix = useQuery<Matrix>({
    queryKey: ['admin', 'products', 'matrix'],
    queryFn: async () => (await apiClient.get<Matrix>('/admin/products/matrix')).data,
  });

  const save = useMutation({
    mutationFn: async (values: ProductFormValues) => {
      if (editing === 'new') return apiClient.post('/admin/products', values);
      // 제품 코드는 서버가 받지 않는다. 권한 키의 접두어라 바꿀 수 없다.
      const { productCode: _unchangeable, ...rest } = values;
      return apiClient.patch(`/admin/products/${(editing as ProductSummary).productCode}`, rest);
    },
    onSuccess: async () => {
      message.success(editing === 'new' ? '제품을 등록했습니다.' : '수정했습니다.');
      setEditing(null);
      await queryClient.invalidateQueries({ queryKey: ['admin', 'products'] });
    },
    onError: (err) => message.error(errorText(err as never)),
  });

  const selectedProduct = useMemo(
    () => (products.data ?? []).find((p) => p.productCode === selected) ?? null,
    [products.data, selected],
  );

  /** 제품 서버가 살아 있는지 확인. 누를 때만 찌른다. */
  const checkHealth = useMutation({
    mutationFn: async (code: string) =>
      (await apiClient.get<Health>(`/admin/products/${code}/health`)).data,
    onSuccess: (health) => {
      if (health.reachable) {
        message.success(`응답 정상 (${health.latencyMs}ms)`);
      } else {
        message.warning(`응답 없음 — ${health.detail ?? '원인 불명'}`);
      }
    },
    onError: (err) => message.error(errorText(err as never)),
  });

  /* ---- 제품 카드 ---- */
  const productCards = (
    <Row gutter={[16, 16]}>
      {(products.data ?? []).map((p) => {
        const isSelected = p.productCode === selected;
        return (
          <Col key={p.productCode} xs={24} sm={12} lg={8} xxl={6}>
            <Card
              hoverable
              onClick={() => setSelected(isSelected ? null : p.productCode)}
              style={{
                borderColor: isSelected ? '#1677ff' : undefined,
                borderWidth: isSelected ? 2 : 1,
                opacity: p.isActive ? 1 : 0.55,
              }}
              styles={{ body: { padding: 16 } }}
            >
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                <Space style={{ justifyContent: 'space-between', width: '100%' }}>
                  <Text strong style={{ fontSize: 16 }}>
                    {p.productName}
                  </Text>
                  <Space size={4}>
                    {!p.isActive && <Tag>중지</Tag>}
                    <Tag color={p.deploymentType === 'PLATFORM' ? 'purple' : 'cyan'}>
                      {p.deploymentType === 'PLATFORM' ? '플랫폼 내장' : '별도 앱'}
                    </Tag>
                  </Space>
                </Space>

                <Text type="secondary" style={{ fontSize: 12, minHeight: 32, display: 'block' }}>
                  {p.description ?? '설명 없음'}
                </Text>

                <Row gutter={8}>
                  <Col span={8}>
                    <Statistic
                      title="구독 기업"
                      value={p.tenantCount}
                      valueStyle={{ fontSize: 20 }}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic title="사용자" value={p.userCount} valueStyle={{ fontSize: 20 }} />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="월 구독"
                      value={Math.round(p.monthlyRevenue).toLocaleString('ko-KR')}
                      valueStyle={{ fontSize: 20 }}
                    />
                  </Col>
                </Row>

                <Space size={4} wrap>
                  {p.attentionCount > 0 && (
                    <Tag color="orange">조치 필요 {p.attentionCount}</Tag>
                  )}
                  {p.trialCount > 0 && <Tag color="blue">체험 {p.trialCount}</Tag>}
                  <Tag>모듈 {p.moduleCount}</Tag>
                  <Tag>요금제 {p.planCount}</Tag>
                </Space>

                <Space
                  size={4}
                  style={{ width: '100%', justifyContent: 'flex-end' }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {p.baseUrl && (
                    <Tooltip title={p.baseUrl}>
                      <Button
                        size="small"
                        type="text"
                        icon={<LinkOutlined />}
                        href={p.baseUrl}
                        target="_blank"
                      />
                    </Tooltip>
                  )}
                  {p.healthUrl && (
                    <Tooltip title="서버 상태 확인">
                      <Button
                        size="small"
                        type="text"
                        icon={<ApiOutlined />}
                        loading={checkHealth.isPending && checkHealth.variables === p.productCode}
                        onClick={() => checkHealth.mutate(p.productCode)}
                      />
                    </Tooltip>
                  )}
                  {p.deploymentType === 'EXTERNAL' && (
                    <Tooltip title="서비스 키">
                      <Button
                        size="small"
                        type="text"
                        icon={<KeyOutlined />}
                        onClick={() => setKeyTarget(p.productCode)}
                      />
                    </Tooltip>
                  )}
                  <Button size="small" type="link" onClick={() => setEditing(p)}>
                    수정
                  </Button>
                </Space>
              </Space>
            </Card>
          </Col>
        );
      })}
    </Row>
  );

  /* ---- 제품별 기업 표 ---- */
  const metricKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const t of tenants.data ?? []) for (const k of Object.keys(t.metrics)) keys.add(k);
    return [...keys].sort();
  }, [tenants.data]);

  const tenantColumns: ColumnsType<ProductTenant> = [
    {
      title: '기업',
      dataIndex: 'companyName',
      fixed: 'left',
      width: 200,
      render: (name: string, row) => (
        <Space direction="vertical" size={0}>
          <Text strong>{name}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {row.slug}
          </Text>
        </Space>
      ),
    },
    {
      title: '구독 상태',
      dataIndex: 'subscriptionStatus',
      width: 110,
      render: (s: string, row) => (
        <Space size={4}>
          <CodeTag codes={codes} group="SUBSCRIPTION_STATUS" value={s} />
          {row.cancelAtPeriodEnd && <Tag color="default">해지 예약</Tag>}
        </Space>
      ),
    },
    { title: '요금제', dataIndex: 'planName', width: 160 },
    { title: '수량', dataIndex: 'units', width: 70, align: 'right' },
    { title: '사용자', dataIndex: 'userCount', width: 80, align: 'right' },
    {
      title: '스토리지',
      dataIndex: 'storageBytes',
      width: 100,
      align: 'right',
      render: (v: number) => formatBytes(v),
    },
    ...metricKeys.map((key) => ({
      title: codes.label('PRODUCT_USAGE_METRIC', key),
      key,
      width: 90,
      align: 'right' as const,
      render: (_: unknown, row: ProductTenant) =>
        key in row.metrics ? row.metrics[key]!.toLocaleString('ko-KR') : '-',
    })),
    {
      title: '제품 보고',
      dataIndex: 'reportedAt',
      width: 120,
      render: (iso: string | null) => {
        const f = reportFreshness(iso);
        return (
          <Tooltip title={iso ? new Date(iso).toLocaleString('ko-KR') : '제품이 한 번도 보고하지 않았습니다'}>
            <Tag color={f.color}>{f.text}</Tag>
          </Tooltip>
        );
      },
    },
    {
      title: '최근 접속',
      dataIndex: 'lastLoginAt',
      width: 110,
      render: (iso: string | null) => formatDate(iso),
    },
    {
      title: '이용 시작',
      dataIndex: 'startedAt',
      width: 110,
      render: (iso: string) => formatDate(iso),
    },
  ];

  /* ---- 매트릭스 ---- */
  const matrixColumns: ColumnsType<Matrix['tenants'][number]> = [
    {
      title: '기업',
      dataIndex: 'companyName',
      fixed: 'left',
      width: 220,
      render: (name: string, row) => (
        <Space direction="vertical" size={0}>
          <Text strong>{name}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {row.slug}
          </Text>
        </Space>
      ),
    },
    {
      title: '기업 상태',
      dataIndex: 'status',
      width: 100,
      render: (s: string) => (
        <Tag color={s === 'ACTIVE' ? 'green' : s === 'SUSPENDED' ? 'red' : 'default'}>{s}</Tag>
      ),
    },
    ...(matrix.data?.products ?? []).map((p) => ({
      title: p.productName,
      key: p.productCode,
      width: 130,
      align: 'center' as const,
      render: (_: unknown, row: Matrix['tenants'][number]) => {
        const sub = row.products[p.productCode];
        if (!sub) return <Text type="secondary">–</Text>;
        return (
          <Tooltip title={sub.planName}>
            <span>
              <CodeTag codes={codes} group="SUBSCRIPTION_STATUS" value={sub.status} />
            </span>
          </Tooltip>
        );
      },
    })),
  ];

  const usingNone = (matrix.data?.tenants ?? []).filter(
    (t) => Object.keys(t.products).length === 0,
  ).length;

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Space style={{ justifyContent: 'space-between', width: '100%' }}>
        <div>
          <Typography.Title level={4} style={{ margin: 0 }}>
            제품 현황
          </Typography.Title>
          <Text type="secondary">기업별로 어떤 시스템을 쓰고 있는지, 얼마나 쓰는지 봅니다.</Text>
        </div>
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => queryClient.invalidateQueries({ queryKey: ['admin', 'products'] })}
          >
            새로고침
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              form.resetFields();
              setEditing('new');
            }}
          >
            제품 등록
          </Button>
        </Space>
      </Space>

      {products.isError && (
        <Alert type="error" showIcon message={errorText(products.error as never)} />
      )}

      {productCards}

      <Tabs
        defaultActiveKey="byProduct"
        items={[
          {
            key: 'byProduct',
            label: '제품별 기업',
            children: selectedProduct ? (
              <Card
                title={`${selectedProduct.productName} 이용 기업`}
                extra={
                  <Space>
                    {selectedProduct.dbName && <Tag>DB {selectedProduct.dbName}</Tag>}
                    <Text type="secondary">{tenants.data?.length ?? 0}곳</Text>
                  </Space>
                }
              >
                {metricKeys.length === 0 && (tenants.data?.length ?? 0) > 0 && (
                  <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message="이 제품은 아직 사용 현황을 보고하지 않았습니다"
                    description={
                      <Paragraph style={{ marginBottom: 0 }}>
                        제품 DB 가 분리되어 있어 플랫폼은 제품 안의 숫자를 직접 알 수 없습니다.
                        제품 서버에 서비스 키를 발급하고{' '}
                        <Text code>POST /api/products/{selectedProduct.productCode}/usage</Text> 로
                        보고하도록 설정하면 여기에 표시됩니다.
                      </Paragraph>
                    }
                  />
                )}
                <Table<ProductTenant>
                  rowKey="tenantId"
                  size="small"
                  loading={tenants.isLoading}
                  dataSource={tenants.data ?? []}
                  columns={tenantColumns}
                  pagination={false}
                  scroll={{ x: 1200 }}
                />
              </Card>
            ) : (
              <Card>
                <Empty description="위에서 제품을 선택하면 이용 기업이 표시됩니다" />
              </Card>
            ),
          },
          {
            key: 'matrix',
            label: '기업 × 제품',
            children: (
              <Card
                title="기업별 이용 시스템"
                extra={
                  usingNone > 0 && (
                    <Badge
                      count={`미이용 ${usingNone}곳`}
                      style={{ backgroundColor: '#faad14' }}
                    />
                  )
                }
              >
                <Table
                  rowKey="tenantId"
                  size="small"
                  loading={matrix.isLoading}
                  dataSource={matrix.data?.tenants ?? []}
                  columns={matrixColumns}
                  pagination={false}
                  scroll={{ x: 800 }}
                />
              </Card>
            ),
          },
        ]}
      />

      <ProductFormModal
        editing={editing}
        form={form}
        saving={save.isPending}
        onCancel={() => setEditing(null)}
        onSubmit={(values) => save.mutate(values)}
      />

      <ProductKeyModal
        productCode={keyTarget}
        onClose={() => setKeyTarget(null)}
        onMessage={message}
        confirm={modal}
      />
    </Space>
  );
}

/* =========================================================================
 * 제품 등록·수정
 * ====================================================================== */

function ProductFormModal({
  editing,
  form,
  saving,
  onCancel,
  onSubmit,
}: {
  editing: ProductSummary | 'new' | null;
  form: FormInstance<ProductFormValues>;
  saving: boolean;
  onCancel: () => void;
  onSubmit: (values: ProductFormValues) => void;
}) {
  const isNew = editing === 'new';

  return (
    <Modal
      open={editing !== null}
      title={isNew ? '제품 등록' : '제품 수정'}
      onCancel={onCancel}
      onOk={() => form.submit()}
      confirmLoading={saving}
      okText="저장"
      cancelText="취소"
      width={640}
      destroyOnClose
      afterOpenChange={(open) => {
        if (open && editing && editing !== 'new') form.setFieldsValue(editing);
      }}
    >
      <Form form={form} layout="vertical" onFinish={onSubmit} style={{ marginTop: 16 }}>
        <Space.Compact style={{ width: '100%', gap: 12 }}>
          <Form.Item
            name="productCode"
            label="제품 코드"
            rules={[
              { required: true, message: '제품 코드를 입력하세요' },
              {
                pattern: /^[a-z][a-z0-9_]{1,29}$/,
                message: '영문 소문자로 시작하는 2~30자 (소문자·숫자·밑줄)',
              },
            ]}
            extra={isNew ? '권한 키의 접두어가 됩니다. 나중에 바꿀 수 없습니다.' : undefined}
            style={{ flex: 1 }}
          >
            <Input disabled={!isNew} placeholder="예: hr" />
          </Form.Item>
          <Form.Item
            name="productName"
            label="제품명"
            rules={[{ required: true, message: '제품명을 입력하세요' }]}
            style={{ flex: 2 }}
          >
            <Input maxLength={150} placeholder="예: WORK&HR" />
          </Form.Item>
        </Space.Compact>

        <Form.Item name="description" label="설명">
          <Input.TextArea rows={2} maxLength={500} />
        </Form.Item>

        <Space.Compact style={{ width: '100%', gap: 12 }}>
          <Form.Item name="deploymentType" label="배포 형태" style={{ width: 160 }}>
            <Select
              options={[
                { value: 'EXTERNAL', label: '별도 앱' },
                { value: 'PLATFORM', label: '플랫폼 내장' },
              ]}
            />
          </Form.Item>
          <Form.Item name="vendor" label="공급사" style={{ flex: 1 }}>
            <Input maxLength={100} />
          </Form.Item>
          <Form.Item name="dbName" label="DB 이름" style={{ flex: 1 }}>
            <Input maxLength={60} placeholder="운영 참고용" />
          </Form.Item>
        </Space.Compact>

        <Form.Item
          name="baseUrl"
          label="제품 주소"
          extra="운영자가 바로 열어볼 수 있는 화면 주소"
        >
          <Input placeholder="https://eco.worknhub.co.kr" />
        </Form.Item>

        <Form.Item
          name="healthUrl"
          label="상태 확인 주소"
          extra="별도 앱은 플랫폼이 멀쩡해도 죽어 있을 수 있습니다"
        >
          <Input placeholder="https://api.eco.worknhub.co.kr/api/health" />
        </Form.Item>

        {/*
          홍보 사이트 요금 안내에서 「자세히 보기」로 연결할 글.
          여기를 비워 두면 링크가 아예 나오지 않는다 — 깨진 링크보다 낫다.
          주소를 코드에 적지 않고 여기에 두는 이유는, 글을 옮기거나 새로 쓸
          때마다 배포하지 않기 위해서다.
        */}
        <Form.Item
          name="marketingUrl"
          label="제품 소개 글 주소"
          extra="홍보 사이트 요금 안내에서 「자세히 보기」로 연결됩니다. 사이트 안 경로(/board/14/107) 또는 전체 주소(https://…). 비우면 링크를 표시하지 않습니다."
          rules={[
            {
              validator: (_, value: string | undefined) =>
                !value || /^(https?:\/\/|\/)/.test(value)
                  ? Promise.resolve()
                  : Promise.reject(new Error('http://, https:// 또는 / 로 시작해야 합니다')),
            },
          ]}
        >
          <Input maxLength={500} placeholder="/board/14/107" />
        </Form.Item>

        <Space.Compact style={{ width: '100%', gap: 12 }}>
          <Form.Item name="iconName" label="아이콘" style={{ flex: 1 }}>
            <Input maxLength={40} placeholder="antd 아이콘 이름" />
          </Form.Item>
          <Form.Item name="sortOrder" label="표시 순서" style={{ width: 130 }}>
            <InputNumber style={{ width: '100%' }} min={0} max={9999} />
          </Form.Item>
          <Form.Item name="isActive" label="판매 중" valuePropName="checked" style={{ width: 100 }}>
            <Switch />
          </Form.Item>
        </Space.Compact>
      </Form>
    </Modal>
  );
}

/* =========================================================================
 * 서비스 키
 * ====================================================================== */

function ProductKeyModal({
  productCode,
  onClose,
  onMessage,
  confirm,
}: {
  productCode: string | null;
  onClose: () => void;
  onMessage: ReturnType<typeof AntdApp.useApp>['message'];
  confirm: ReturnType<typeof AntdApp.useApp>['modal'];
}) {
  const queryClient = useQueryClient();
  const [issued, setIssued] = useState<{ secret: string; keyLabel: string } | null>(null);
  const [label, setLabel] = useState('');
  /*
   * 기본은 **살아 있는 키만** 본다.
   *
   * 폐기한 키는 지우지 않고 남으므로 검증을 돌릴 때마다 쌓인다. 실제로
   * 28개 중 26개가 폐기 상태가 되어, 「지금 유효한 키가 무엇인가」 를
   * 한눈에 볼 수 없었다. 지난 키가 필요할 때만 켠다.
   */
  const [includeRevoked, setIncludeRevoked] = useState(false);

  const keys = useQuery<ProductKey[]>({
    queryKey: ['admin', 'products', productCode, 'keys', includeRevoked],
    queryFn: async () =>
      (
        await apiClient.get<ProductKey[]>(`/admin/products/${productCode}/keys`, {
          params: { includeRevoked: includeRevoked ? 'true' : 'false' },
        })
      ).data,
    enabled: !!productCode,
  });

  const issue = useMutation({
    mutationFn: async () =>
      (
        await apiClient.post<{ secret: string; keyLabel: string }>(
          `/admin/products/${productCode}/keys`,
          { label, expiresInDays: null },
        )
      ).data,
    onSuccess: async (result) => {
      setIssued(result);
      setLabel('');
      await queryClient.invalidateQueries({ queryKey: ['admin', 'products', productCode, 'keys'] });
    },
    onError: (err) => onMessage.error(errorText(err as never)),
  });

  const revoke = useMutation({
    mutationFn: async (id: string) =>
      // axios 의 delete 는 두 번째 인자가 config 다. 본문은 data 안에 넣어야 한다.
      apiClient.delete(`/admin/products/keys/${id}`, { data: { reason: '운영자 폐기' } }),
    onSuccess: async () => {
      onMessage.success('키를 폐기했습니다.');
      await queryClient.invalidateQueries({ queryKey: ['admin', 'products', productCode, 'keys'] });
    },
    onError: (err) => onMessage.error(errorText(err as never)),
  });

  return (
    <Modal
      open={!!productCode}
      title={`${productCode} 서비스 키`}
      onCancel={() => {
        setIssued(null);
        onClose();
      }}
      footer={null}
      width={720}
      destroyOnClose
    >
      <Paragraph type="secondary">
        제품 서버가 사용 현황을 보고할 때 쓰는 키입니다. 이 키로 할 수 있는 일은 사용량 보고뿐이며,
        고객 데이터에는 접근할 수 없습니다.
      </Paragraph>

      {issued && (
        <Alert
          type="success"
          showIcon
          style={{ marginBottom: 16 }}
          message="키가 발급되었습니다 — 지금 복사하세요"
          description={
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text type="warning">이 화면을 닫으면 다시 볼 수 없습니다.</Text>
              <Input.TextArea value={issued.secret} readOnly autoSize />
              <Button
                size="small"
                onClick={() => {
                  void navigator.clipboard.writeText(issued.secret);
                  onMessage.success('복사했습니다.');
                }}
              >
                복사
              </Button>
            </Space>
          }
        />
      )}

      <Space.Compact style={{ width: '100%', marginBottom: 16 }}>
        <Input
          placeholder="키 용도 (예: 운영 서버 야간 배치)"
          value={label}
          maxLength={100}
          onChange={(e) => setLabel(e.target.value)}
          onPressEnter={() => label.trim() && issue.mutate()}
        />
        <Button
          type="primary"
          loading={issue.isPending}
          disabled={!label.trim()}
          onClick={() => issue.mutate()}
        >
          발급
        </Button>
      </Space.Compact>

      <div style={{ margin: '12px 0 4px', textAlign: 'right' }}>
        <Checkbox
          checked={includeRevoked}
          onChange={(e) => setIncludeRevoked(e.target.checked)}
        >
          폐기한 키 포함
        </Checkbox>
      </div>

      <Table<ProductKey>
        rowKey="productKeyId"
        size="small"
        loading={keys.isLoading}
        dataSource={keys.data ?? []}
        pagination={false}
        locale={{
          emptyText: includeRevoked
            ? '발급한 키가 없습니다.'
            : '사용 중인 키가 없습니다. 「폐기한 키 포함」을 켜면 지난 키를 볼 수 있습니다.',
        }}
        columns={[
          { title: '용도', dataIndex: 'keyLabel' },
          {
            title: '키',
            dataIndex: 'keyPrefix',
            width: 140,
            render: (p: string) => <Text code>{p}…</Text>,
          },
          {
            title: '마지막 사용',
            dataIndex: 'lastUsedAt',
            width: 120,
            render: (iso: string | null) =>
              iso ? formatDate(iso) : <Text type="secondary">사용 없음</Text>,
          },
          {
            title: '상태',
            width: 90,
            render: (_, row) =>
              row.revokedAt ? <Tag>폐기</Tag> : <Tag color="green">사용중</Tag>,
          },
          {
            title: '',
            width: 70,
            render: (_, row) =>
              !row.revokedAt && (
                <Button
                  size="small"
                  type="link"
                  danger
                  onClick={() =>
                    confirm.confirm({
                      title: '키를 폐기할까요?',
                      content: '이 키를 쓰는 제품 서버는 즉시 보고에 실패합니다.',
                      okText: '폐기',
                      cancelText: '취소',
                      okButtonProps: { danger: true },
                      onOk: () => revoke.mutate(row.productKeyId),
                    })
                  }
                >
                  폐기
                </Button>
              ),
          },
        ]}
      />
    </Modal>
  );
}
